'use client';

import * as React from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { formatStatValue, stripHtml } from '@/lib/homepage-mappers';
import { getStrapiMediaUrl, getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type {
  AboutGridImageTextItem,
  AboutStatSection,
  AboutStatsWithImageGrid,
} from '@/types/aboutus';

type AboutStoryStatsSectionProps = {
  zone?: AboutStatsWithImageGrid;
  content?: string;
  /** @deprecated Legacy stats zone — prefer `zone.gridImageText`. */
  statsZone?: AboutStatSection;
};

type DisplayStat = {
  value: string;
  label: string;
};

const FALLBACK_STATS: DisplayStat[] = [
  { value: '13+', label: 'Years of excellence' },
  { value: '7', label: 'Industrial facilities' },
  { value: '5000+', label: 'Dealer network' },
  { value: '2L+', label: 'Happy families' },
];

const STORY_IMAGES = {
  main: '/images/home_img1.png',
  finish: '/images/product_img1.png',
  detail: '/images/building.png',
  built: '/images/about_new.png',
};

function getGridImageSrc(item: AboutGridImageTextItem | undefined, fallback: string) {
  const media = item?.media ?? null;
  return getStrapiMediaUrl(media, 'large') || getStrapiOriginalMediaUrl(media) || fallback;
}

function StoryCardMedia({
  item,
  fallback,
  alt,
  sizes,
  className,
}: {
  item: AboutGridImageTextItem | undefined;
  fallback: string;
  alt: string;
  sizes: string;
  className?: string;
}) {
  const src = getGridImageSrc(item, fallback);

  return (
    <CmsImage
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      className={className}
      fallbackSrc={MEDIA_PLACEHOLDER}
    />
  );
}

function getGridImageAlt(item: AboutGridImageTextItem | undefined, fallback: string) {
  return item?.media?.alternativeText?.trim() || stripHtml(item?.description) || fallback;
}

function isStatHeading(heading: string | null | undefined) {
  return Boolean(heading && /\d/.test(heading));
}

function splitStatValue(value: string) {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d+(?:\.\d+)?)(.*)$/);

  if (!match) {
    return {
      number: 0,
      suffix: trimmed,
      decimals: 0,
      isNumeric: false,
    };
  }

  const [, numericValue, suffix = ''] = match;
  return {
    number: Number(numericValue),
    suffix,
    decimals: numericValue.includes('.') ? numericValue.split('.')[1]?.length || 0 : 0,
    isNumeric: true,
  };
}

function CounterValue({ value }: { value: string }) {
  const valueRef = React.useRef<HTMLSpanElement | null>(null);

  React.useEffect(() => {
    const node = valueRef.current;
    if (!node || typeof window === 'undefined') return;

    const parsed = splitStatValue(value);
    if (!parsed.isNumeric) {
      node.textContent = value;
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const state = { value: 0 };
    const tween = gsap.to(state, {
      value: parsed.number,
      duration: 1.6,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: node,
        start: 'top 88%',
        once: true,
      },
      onUpdate: () => {
        node.textContent = `${state.value.toFixed(parsed.decimals)}${parsed.suffix}`;
      },
      onComplete: () => {
        node.textContent = value;
      },
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [value]);

  return <span ref={valueRef}>0</span>;
}

/**
 * Decode common entities without collapsing whitespace. Needed before we
 * split on paragraph boundaries — `stripHtml` replaces every `\s+` run with a
 * single space, which erases the CMS paragraph breaks.
 */
function decodeBasicEntities(input: string): string {
  return input
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)));
}

/**
 * Split CMS rich-text into display paragraphs.
 *
 * Authors typically enter multiple paragraphs. Strapi may ship that as:
 * - several `<p>…</p>` blocks (sometimes with empty `&nbsp;` spacers between)
 * - one `<p>` with `<br><br>` hard breaks
 * - plain text with blank lines
 *
 * Using `stripHtml` on the whole string first collapsed those into a single run.
 * Marker-based split avoids `matchAll` (needs downlevelIteration) and keeps
 * paragraph boundaries intact through tag stripping.
 */
function toStoryParagraphs(html: string | null | undefined): string[] {
  if (!html?.trim()) return [];

  const BREAK = '\u0001';

  const normalized = decodeBasicEntities(html.trim())
    // Block / hard-break boundaries → marker
    .replace(/<\/p\s*>/gi, BREAK)
    .replace(/<p\b[^>]*>/gi, '')
    .replace(/<br\s*\/?>\s*(?:<br\s*\/?>)+/gi, BREAK)
    .replace(/<\/(?:div|h[1-6]|li|blockquote)\s*>/gi, BREAK)
    .replace(/\n\s*\n+/g, BREAK)
    // Soft single <br> stays within a paragraph
    .replace(/<br\s*\/?>/gi, ' ')
    // Drop remaining tags, then collapse whitespace (the U+0001 BREAK marker
    // is a control char, not whitespace, so \s+ leaves it intact for split()).
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ');

  return normalized
    .split(BREAK)
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export default function AboutStoryStatsSection({
  zone,
  content = '',
  statsZone,
}: AboutStoryStatsSectionProps) {
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const statsRef = React.useRef<HTMLDivElement | null>(null);
  const imageGridRef = React.useRef<HTMLDivElement | null>(null);

  const title = zone?.heading?.trim() || 'Our story';
  // Each paragraph keeps its own word list so the gsap word-reveal still works
  // while paragraph breaks survive. Prefer raw CMS HTML so we can split on real
  // block boundaries — the pre-stripped `content` prop is only a last resort.
  const paragraphs = React.useMemo<string[][]>(() => {
    const fromHtml = toStoryParagraphs(zone?.description);
    const fromPlain = content ? toStoryParagraphs(content) : [];
    const source = fromHtml.length > 0 ? fromHtml : fromPlain;
    return source.map((block) => block.split(/\s+/).filter(Boolean)).filter((w) => w.length > 0);
  }, [zone?.description, content]);
  const totalWords = React.useMemo(
    () => paragraphs.reduce((sum, words) => sum + words.length, 0),
    [paragraphs],
  );
  const gridItems = React.useMemo(() => zone?.gridImageText ?? [], [zone?.gridImageText]);

  const stats = React.useMemo<DisplayStat[]>(() => {
    const statCounter = zone?.statCounter ?? [];
    const fromStatCounter = statCounter
      .map((stat) => ({
        value: formatStatValue(stat.statValue, stat.statSuffix),
        label: stripHtml(stat.statDescription).trim(),
      }))
      .filter((s) => Boolean(s.value) && Boolean(s.label));

    // Prefer `statCounter` when present because it is the source of truth
    // for the animated 4-stat row.
    if (fromStatCounter.length > 0) {
      return [...fromStatCounter, ...FALLBACK_STATS].slice(0, 4);
    }

    const fromGrid = gridItems
      .filter((item) => isStatHeading(item.heading))
      .map((item) => ({
        value: item.heading!.trim(),
        label: stripHtml(item.description),
      }))
      .filter((item) => item.label);

    if (fromGrid.length > 0) {
      return [...fromGrid, ...FALLBACK_STATS].slice(0, 4);
    }

    if (statsZone?.stats && statsZone.stats.length > 0) {
      return statsZone.stats.slice(0, 4).map((stat) => ({
        value: formatStatValue(stat.statValue, stat.statSuffix),
        label: stripHtml(stat.statDescription).trim(),
      }));
    }

    return FALLBACK_STATS;
  }, [gridItems, statsZone, zone?.statCounter]);

  React.useLayoutEffect(() => {
    const section = sectionRef.current;
    const imageGrid = imageGridRef.current;
    if (!section || !imageGrid) return;

    const ctx = gsap.context(() => {
      const titleEl = section.querySelector('[data-story-title]');
      if (titleEl) {
        gsap.fromTo(
          titleEl,
          { y: '105%', immediateRender: false },
          {
            y: '0%',
            duration: 0.9,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 82%',
              once: true,
              invalidateOnRefresh: true,
            },
          },
        );
      }

      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
        gsap.fromTo(
          imageGrid,
          { y: 0, immediateRender: false },
          {
            y: -40,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              invalidateOnRefresh: true,
            },
          },
        );
      }
    }, section);

    return () => ctx.revert();
  }, []);

  React.useEffect(() => {
    const section = sectionRef.current;
    const contentNode = contentRef.current;
    if (!section || !contentNode || typeof window === 'undefined') return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const words = contentNode.querySelectorAll('[data-story-word]');
      gsap.set(words, { color: 'rgba(17,17,17,0.22)' });
      gsap.to(words, {
        color: '#111111',
        ease: 'none',
        stagger: 0.035,
        scrollTrigger: {
          trigger: contentNode,
          start: 'top 82%',
          end: 'bottom 52%',
          scrub: true,
        },
      });

      gsap.from('[data-story-card]', {
        y: 28,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 72%',
          once: true,
        },
      });

      if (statsRef.current) {
        gsap.from(statsRef.current.querySelectorAll('[data-story-stat]'), {
          y: 18,
          opacity: 0,
          duration: 0.7,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: statsRef.current,
            start: 'top 88%',
            once: true,
          },
        });
      }
    }, section);

    return () => ctx.revert();
  }, [totalWords]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white text-[#111]"
      data-page-reveal-skip
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-35"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
        }}
      />
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid gap-8 md:gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-12 xl:gap-16">
          <div className="max-w-2xl">
            <div className="overflow-hidden">
              <h2
                data-story-title
                className="text-3xl font-medium !leading-tight tracking-[-0.04em] text-[#ed1c22] sm:text-4xl md:text-5xl lg:text-[4.25rem]"
              >
                {title}
              </h2>
            </div>

            <div ref={contentRef} className="mt-6 max-w-xl md:mt-8">
              {paragraphs.map((words, pIndex) => (
                <p
                  key={pIndex}
                  className={`text-sm font-medium leading-7 text-[#111]/25 sm:text-base sm:leading-8 md:text-lg md:leading-9 ${
                    pIndex > 0 ? 'mt-4 sm:mt-5' : ''
                  }`}
                >
                  {words.map((word, index) => (
                    <React.Fragment key={index}>
                      <span data-story-word>{word}</span>
                      {index < words.length - 1 ? ' ' : ''}
                    </React.Fragment>
                  ))}
                </p>
              ))}
            </div>

            <div
              ref={statsRef}
              className="mt-8 grid grid-cols-2 gap-x-5 gap-y-6 border-t border-[#111]/10 pt-6 sm:mt-9 sm:grid-cols-4 sm:gap-x-4 sm:gap-y-7 sm:border-t-0 sm:pt-7 md:gap-x-5"
            >
              {stats.map((stat, index) => (
                <article
                  key={stat.label}
                  data-story-stat
                  className="relative min-h-20 pl-5 before:absolute before:left-0 before:top-1 before:h-9 before:w-px before:bg-[#111]/10 before:content-[''] odd:pl-0 odd:before:hidden sm:min-h-24 sm:pl-4 sm:odd:pl-4 sm:odd:before:block sm:first:pl-0 sm:first:before:hidden md:pl-5"
                >
                  <p className="text-2xl font-medium leading-none tracking-[-0.04em] text-[#111] sm:text-3xl md:text-4xl">
                    <CounterValue value={stat.value} />
                  </p>
                  <p className="mt-2 max-w-28 text-[0.68rem] font-medium leading-snug text-[#111]/45 sm:text-[0.7rem]">
                    {stat.label}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div
            ref={imageGridRef}
            className="mt-8 grid min-h-[520px] grid-cols-2 grid-rows-[1.2fr_0.75fr_0.75fr] gap-3 sm:min-h-[600px] sm:gap-4 md:min-h-[455px] md:grid-cols-[1.12fr_1fr_0.82fr] md:grid-rows-[1fr_0.78fr] lg:min-h-[455px]"
          >
            <article
              data-story-card
              className="group relative col-span-2 h-full min-h-[180px] overflow-hidden rounded-[1.15rem] bg-[#f3f3f3] sm:min-h-[220px] sm:rounded-[1.35rem] md:col-span-1 md:row-span-2 md:min-h-0"
            >
              <StoryCardMedia
                item={gridItems[0]}
                fallback={STORY_IMAGES.main}
                alt={getGridImageAlt(gridItems[0], 'Wonder Cement homes built to last')}
                sizes="(min-width: 1024px) 24vw, (min-width: 768px) 34vw, 92vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
                {gridItems[0]?.heading ? (
                  <p className="mb-2 inline-flex items-center rounded-full bg-black/55 px-3 py-1 text-[0.58rem] font-bold uppercase tracking-[0.2em] text-white backdrop-blur sm:mb-3 sm:text-[0.62rem] sm:tracking-[0.22em]">
                    <span className="mr-2 h-1.5 w-1.5 rounded-full bg-[#ed1c25]" />
                    {gridItems[0].heading}
                  </p>
                ) : null}
                <h3 className="max-w-xs text-lg font-semibold leading-tight text-white sm:text-xl">
                  {stripHtml(gridItems[0]?.description) || 'Dream homes built to last'}
                </h3>
              </div>
            </article>

            <article
              data-story-card
              className="relative h-full min-h-[140px] overflow-hidden rounded-[1.15rem] bg-[#f3f3f3] sm:min-h-[160px] sm:rounded-[1.35rem]"
            >
              <StoryCardMedia
                item={gridItems[1]}
                fallback={STORY_IMAGES.finish}
                alt={getGridImageAlt(gridItems[1], 'Premium finish with Wonder Cement')}
                sizes="(min-width: 1024px) 18vw, (min-width: 768px) 28vw, 46vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/5" />
              <p className="absolute bottom-4 left-4 right-4 text-xs font-semibold text-white sm:bottom-5 sm:left-5 sm:right-5 sm:text-sm">
                {stripHtml(gridItems[1]?.description) || 'Premium Finish'}
              </p>
            </article>

            <article
              data-story-card
              className="relative flex flex-col justify-center overflow-hidden rounded-[1.15rem] bg-[#111] p-4 shadow-2xl shadow-black/20 sm:rounded-[1.35rem] sm:p-5"
            >
              <span className="absolute right-4 top-4 h-5 w-5 rounded-full bg-[#ed1c25]/20 sm:right-5 sm:top-5">
                <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ed1c25]" />
              </span>
              <p className="text-2xl font-medium tracking-[-0.04em] text-white sm:text-3xl">
                {gridItems[3]?.heading || '5000+'}
              </p>
              <p className="mt-2 text-[0.66rem] font-medium leading-snug text-white/45 sm:text-[0.7rem]">
                {stripHtml(gridItems[3]?.description) || 'Trusted dealers across Bharat'}
              </p>
            </article>

            <article
              data-story-card
              className="relative h-full min-h-[140px] overflow-hidden rounded-[1.15rem] bg-[#ed1c25] sm:min-h-[160px] sm:rounded-[1.35rem]"
            >
              <StoryCardMedia
                item={gridItems[2]}
                fallback={STORY_IMAGES.detail}
                alt={getGridImageAlt(gridItems[2], 'Years of Wonder Cement excellence')}
                sizes="(min-width: 1024px) 18vw, (min-width: 768px) 28vw, 46vw"
                className="object-cover opacity-35"
              />
              <div className="absolute inset-0 bg-[#ed1c25]/75" />
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-5">
                <p className="text-2xl font-medium tracking-[-0.05em] text-white sm:text-3xl">
                  {gridItems[2]?.heading || '13+'}
                </p>
                <p className="mt-1 text-[0.68rem] font-medium text-white/75 sm:text-xs">
                  {stripHtml(gridItems[2]?.description) || 'Years of Excellence'}
                </p>
              </div>
            </article>

            <article
              data-story-card
              className="relative h-full min-h-[140px] overflow-hidden rounded-[1.15rem] bg-[#f3f3f3] sm:min-h-[160px] sm:rounded-[1.35rem]"
            >
              <StoryCardMedia
                item={gridItems[4]}
                fallback={STORY_IMAGES.built}
                alt={getGridImageAlt(gridItems[4], 'Built right with Wonder Cement')}
                sizes="(min-width: 1024px) 18vw, (min-width: 768px) 28vw, 46vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 text-xs font-semibold text-white sm:bottom-5 sm:left-5 sm:right-5 sm:text-sm">
                {stripHtml(gridItems[4]?.description) || 'Built Right'}
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
